/* FilterChips — the row of active-filter pills under a filter card.
 *
 * Every redesigned list page draws the same thing (`docs/design/photo-review.html`,
 * `visits.html`): one `<Tag closable>` per applied filter, reading `Label: value`, and clicking
 * the × clears that one filter. It is a component rather than a copied block because the pill
 * shape and the brand tint are not something antd's `<Tag>` can express — its own `color`
 * presets are the status palette, which this is not — so a second copy would mean a second
 * definition of those styles going out of sync.
 *
 * ```jsx
 * const { remember, nameFor } = useLabelCache();
 * <FilterChips items={chips} onClose={clearChip} closeLabel={t("a_remove_filter")} />
 * ```
 */
import React, { useCallback, useRef, useState } from "react";
import { Flex, Tag } from "antd";
import { CloseOutlined } from "@ant-design/icons";

import "./filter-chips.scss";

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

/**
 * Names for filter values that arrived as bare ids.
 *
 * A shared or bookmarked URL carries ids only, so a restored filter holds `{ id: 52 }` and its
 * chip could otherwise only say "52". `EntitySelect`'s `onResolve` hands over the option objects
 * once it has fetched them; this keeps them.
 *
 * Deliberately NOT stored in the filter state itself: the request is memoised on that state, so
 * patching a label into it would refetch the whole list just to relabel a chip.
 */
export function useLabelCache() {
  const [labels, setLabels] = useState({});

  const remember = useCallback(
    key => list =>
      setLabels(prev => {
        const next = { ...prev };
        let changed = false;
        (list || []).forEach(option => {
          const cacheKey = `${key}:${idOf(option)}`;
          const name = option && typeof option === "object" ? option.label || option.name : null;
          if (name && next[cacheKey] !== name) {
            next[cacheKey] = name;
            changed = true;
          }
        });
        return changed ? next : prev;
      }),
    []
  );

  /* ⚠ The first step must only accept an OBJECT's own name. A bare id is a truthy non-object, so
     a `nameOf`-style helper that returns non-objects unchanged short-circuits the `||` and the
     cache is never consulted. */
  const cache = useRef(labels);
  cache.current = labels;
  const nameFor = useCallback((key, value) => {
    const own = value && typeof value === "object" ? value.label || value.name : null;
    return own || cache.current[`${key}:${idOf(value)}`] || String(idOf(value) ?? "");
  }, []);

  return { labels, remember, nameFor };
}

/**
 * @param items  `[{ key, id?, label, value }]` — `key` identifies the filter, `id` the single
 *               value within a multi-value one. Both are handed back to `onClose`.
 */
export default function FilterChips({ items, onClose, closeLabel, className = "" }) {
  if (!items?.length) return null;
  return (
    <Flex className={`fchips ${className}`} gap="small" wrap flex="1">
      {items.map(chip => (
        <Tag
          key={`${chip.key}-${chip.id ?? chip.value}`}
          className="fchip"
          closable
          closeIcon={<CloseOutlined aria-label={closeLabel} />}
          onClose={() => onClose(chip.key, chip.id)}>
          {chip.label}: {chip.value}
        </Tag>
      ))}
    </Flex>
  );
}
